/**
 * Single source of truth for resolving any stored media URL to a renderable
 * `<img src>` value.
 *
 * Why this exists
 * ---------------
 * Historical product / slider / page documents in MongoDB still reference
 * media as `/api/media/serve/<key>` — a path that used to be served by the
 * backend's local-disk fallback. After the S3 + CloudFront migration those
 * local files no longer exist on the pod, so the relative path 404s.
 *
 * The CDN host is read at runtime from the admin-managed
 * `general_settings.media.cdnBaseUrl` value (see initMediaConfig()), so the
 * admin can rotate the CDN without a redeploy. Falls back to the build-time
 * `REACT_APP_CDN_BASE_URL` env var (if set) and finally to the legacy
 * backend-served path.
 */

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const ENV_CDN = (process.env.REACT_APP_CDN_BASE_URL || '').replace(/\/$/, '');

// Mutable, populated once at app boot by `initMediaConfig` below.
let CDN_BASE_URL = ENV_CDN;

/** Allow runtime updates (admin saves new value → no full page reload needed). */
export const setCdnBaseUrl = (url) => {
  CDN_BASE_URL = (url || '').replace(/\/$/, '') || ENV_CDN;
};

export const getCdnBaseUrl = () => CDN_BASE_URL;

const LEGACY_PREFIX = /^\/?api\/media\/serve\//;

export const resolveMediaUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;

  if (LEGACY_PREFIX.test(url)) {
    const key = url.replace(LEGACY_PREFIX, '');
    if (CDN_BASE_URL) return `${CDN_BASE_URL}/${key}`;
    return `${BACKEND_URL}/api/media/serve/${key}`;
  }

  // Generic relative path (logos, static placeholders, etc.)
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

export const resolveImageRecord = (image, size = 'thumbnail') => {
  if (!image) return '';
  if (typeof image === 'string') return resolveMediaUrl(image);

  const opt = image.optimized?.[size]?.jpg?.url || image.optimized?.[size]?.url;
  if (opt) return resolveMediaUrl(opt);
  if (image[size]) return resolveMediaUrl(image[size]);
  return resolveMediaUrl(
    image.original || image.url || image.large || image.medium || image.thumbnail || ''
  );
};

/**
 * Boot-time hydration. Called once from App.js so the resolver knows the
 * configured CDN host before any image renders.
 */
let _initPromise = null;
export const initMediaConfig = () => {
  if (_initPromise) return _initPromise;
  const url = `${BACKEND_URL}/api/general-settings/general`;
  _initPromise = fetch(url, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
      const cdn = data?.media?.cdnBaseUrl;
      if (cdn) setCdnBaseUrl(cdn);
      return getCdnBaseUrl();
    })
    .catch(() => getCdnBaseUrl());
  return _initPromise;
};

export default resolveMediaUrl;
